import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { startOfDay } from "date-fns"
import { Calendar } from "lucide-react"
import { type UseFormReturn, useForm, useWatch } from "react-hook-form"
import { z } from "zod"
import { BookingsService } from "@/client"
import type { BookingUpdateRequest } from "@/client/types.gen"
import DietaryRequirementsForm from "@/components/Booking/DietaryRequirementsForm"
import GuestInformationForm from "@/components/Booking/GuestInformationForm"
import KayakRentalForm from "@/components/Booking/KayakRentalForm"
import WaterTaxiForm from "@/components/Booking/WaterTaxiForm"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { LoadingButton } from "@/components/ui/loading-button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ALLERGY_SEVERITY_VALUES,
  DIETARY_TYPE_VALUES,
  EQUIPMENT_SIZE_VALUES,
  EQUIPMENT_TYPE_VALUES,
  SCHEDULED_TIME_VALUES,
  TRIP_TYPE_VALUES,
} from "@/constants/booking"
import useCustomToast from "@/hooks/useCustomToast"
import {
  bookingDetailToFormValues,
  bookingFormValuesToRequest,
  formatLodgeStayFormRange,
} from "@/lib/booking-mappers"
import { getLodgeStayValidationIssues } from "@/lib/booking-validation"

export const Route = createFileRoute("/_layout/bookings/$id/edit")({
  component: EditBookingPage,
  staticData: {
    pageTitle: "Edit Booking",
    pageIcon: Calendar,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [
      {
        title: "Edit Booking - Broken Islands Booking",
      },
    ],
  }),
})

// Dietary requirement schema
const dietaryRequirementSchema = z.object({
  guestName: z.string().min(1, "Guest name is required"),
  meals: z.object({
    breakfast: z.boolean(),
    lunch: z.boolean(),
    dinner: z.boolean(),
  }),
  camperLunches: z.object({
    arrival: z.boolean(),
    departure: z.boolean(),
  }),
  dietaryType: z.enum(DIETARY_TYPE_VALUES),
  allergies: z.string().optional(),
  allergySeverity: z.enum(ALLERGY_SEVERITY_VALUES).optional(),
  notes: z.string().optional(),
})

// Kayak rental schema
const kayakRentalSchema = z
  .object({
    guestName: z.string().min(1, "Guest name is required"),
    equipmentType: z.enum(EQUIPMENT_TYPE_VALUES, {
      message: "Equipment type is required",
    }),
    equipmentSize: z.enum(EQUIPMENT_SIZE_VALUES, {
      message: "Equipment size is required",
    }),
    pickupDate: z.date({ message: "Pickup date is required" }),
    pickupTime: z.string().optional(),
    returnDate: z.date({ message: "Return date is required" }),
    returnTime: z.string().optional(),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      return startOfDay(data.returnDate) >= startOfDay(data.pickupDate)
    },
    {
      message: "Return date must be on or after pickup date",
      path: ["returnDate"],
    },
  )

// Water taxi trip schema
const waterTaxiTripSchema = z
  .object({
    tripType: z.enum(TRIP_TYPE_VALUES, {
      message: "Trip type is required",
    }),
    timeSelection: z.enum(["scheduled", "custom"], {
      message: "Time selection is required",
    }),
    scheduledTime: z.enum(SCHEDULED_TIME_VALUES).optional(),
    customTime: z.string().optional(),
    pickupLocation: z.string().optional(),
    dropoffLocation: z.string().optional(),
    passengerCount: z
      .number({ message: "Passenger count is required" })
      .min(1, "At least 1 passenger is required")
      .max(12, "Water taxi capacity is 12 maximum"),
    kayakCount: z
      .number()
      .min(0, "Kayak count cannot be negative")
      .max(12, "Maximum 12 kayaks per taxi"),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.timeSelection === "scheduled") {
        return !!data.scheduledTime
      }
      return true
    },
    {
      message: "Scheduled time is required for scheduled transfers",
      path: ["scheduledTime"],
    },
  )
  .refine(
    (data) => {
      if (data.timeSelection === "custom") {
        return !!data.customTime
      }
      return true
    },
    {
      message: "Custom time is required for custom transfers",
      path: ["customTime"],
    },
  )
  .refine(
    (data) => {
      if (data.tripType === "custom" || data.timeSelection === "custom") {
        return !!data.pickupLocation && data.pickupLocation.length > 0
      }
      return true
    },
    {
      message: "Pickup location is required for custom trips or times",
      path: ["pickupLocation"],
    },
  )
  .refine(
    (data) => {
      if (data.tripType === "custom" || data.timeSelection === "custom") {
        return !!data.dropoffLocation && data.dropoffLocation.length > 0
      }
      return true
    },
    {
      message: "Dropoff location is required for custom trips or times",
      path: ["dropoffLocation"],
    },
  )

// Booking form schema (same as new booking but arrival date validation is relaxed for editing)
export const bookingFormSchema = z
  .object({
    guestType: z.enum(["lodge", "camping", "mixed"], {
      message: "Guest type is required",
    }),
    primaryContactName: z
      .string()
      .min(1, "Primary contact name is required")
      .max(200, "Name must be 200 characters or less"),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Please enter a valid email address"),
    phone: z.string().optional(),
    arrivalDate: z.date({ message: "Arrival date is required" }),
    departureDate: z.date({ message: "Departure date is required" }),
    numberOfGuests: z
      .number({ message: "Number of guests is required" })
      .min(1, "At least 1 guest is required")
      .max(50, "Maximum 50 guests allowed"),
    lodgeStays: z.array(
      z.object({
        startDate: z.date({ message: "Lodge start date is required" }),
        endDate: z.date({ message: "Lodge end date is required" }),
      }),
    ),
    waterTaxiTrips: z.array(waterTaxiTripSchema),
    kayakRentals: z.array(kayakRentalSchema),
    dietaryRequirements: z.array(dietaryRequirementSchema),
  })
  .refine(
    (data) => {
      return startOfDay(data.departureDate) >= startOfDay(data.arrivalDate)
    },
    {
      message: "Departure date must be on or after arrival date",
      path: ["departureDate"],
    },
  )
  .superRefine((data, ctx) => {
    for (const issue of getLodgeStayValidationIssues(data)) {
      ctx.addIssue({ code: "custom", ...issue })
    }
  })

type FormValues = z.infer<typeof bookingFormSchema>

/**
 * Renders placeholder skeleton UI for the Edit Booking page while booking data is loading.
 *
 * @returns A JSX element containing layout-aligned skeletons that match the edit form structure.
 */
function EditBookingPageSkeleton() {
  return (
    <div className="container max-w-5xl py-6">
      <div className="space-y-8">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
          <div className="space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
          <div className="md:col-span-2 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Render the Kitchen section for every booking type.
 *
 * Displays kitchen-planning context for camping and mixed bookings along with
 * the dietary requirements form.
 *
 * @param form - The react-hook-form instance for the booking form
 * @param disabled - If `true`, the contained DietaryRequirementsForm is rendered in a disabled state
 * @returns The Kitchen section's JSX markup
 */
function DietaryRequirementsSection({
  form,
  disabled,
}: {
  form: UseFormReturn<FormValues>
  disabled: boolean
}) {
  const guestType = useWatch({ control: form.control, name: "guestType" })
  const lodgeStays = useWatch({
    control: form.control,
    name: "lodgeStays",
  })
  const arrivalDate = useWatch({ control: form.control, name: "arrivalDate" })
  const departureDate = useWatch({
    control: form.control,
    name: "departureDate",
  })
  const lodgeStayLabels = lodgeStays
    .map(formatLodgeStayFormRange)
    .filter((label): label is string => label !== null)

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
      <div className="px-4 sm:px-0">
        <h2 className="text-base/7 font-semibold text-foreground">Kitchen</h2>
        <p className="mt-1 text-sm/6 text-muted-foreground">
          Capture dietary needs and allergies for kitchen meal planning.
        </p>
        {guestType === "mixed" && lodgeStayLabels.length > 0 && (
          <div className="mt-2 space-y-1 text-sm font-medium text-primary">
            <p>Meals apply to these lodge portions:</p>
            {lodgeStayLabels.map((label, index) => (
              <p key={`${label}-${index}`}>{label}</p>
            ))}
          </div>
        )}
        {guestType === "camping" && (
          <p className="mt-2 text-sm font-medium text-primary">
            Only explicitly selected arrival or departure lunches appear in the
            Kitchen calendar.
          </p>
        )}
      </div>

      <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
        <div className="px-4 py-6 sm:p-8">
          <DietaryRequirementsForm
            bookingType={guestType}
            arrivalDate={arrivalDate}
            departureDate={departureDate}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Renders the Edit Booking page for the booking identified by the route `id`.
 *
 * Displays a multi-section form populated from the booking data (guest information, water taxi, kayak rentals, dietary requirements),
 * handles validation and submission to update the booking, and provides loading and error states.
 *
 * @returns The React element for the Edit Booking page.
 */
function EditBookingPage() {
  const router = useRouter()
  const { id } = Route.useParams()
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  // Fetch the existing booking
  const {
    data: booking,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["booking", id],
    queryFn: () => BookingsService.getBooking({ bookingId: id }),
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: BookingUpdateRequest) =>
      BookingsService.updateBooking({ bookingId: id, requestBody: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] })
      queryClient.invalidateQueries({ queryKey: ["booking", id] })
      showSuccessToast("Booking updated successfully")
      router.navigate({ to: "/bookings/$id", params: { id } })
    },
    onError: () => {
      showErrorToast("Failed to update booking")
    },
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(bookingFormSchema),
    mode: "onBlur",
    criteriaMode: "all",
    values: booking ? bookingDetailToFormValues(booking) : undefined,
  })

  const onSubmit = (data: FormValues) => {
    const request: BookingUpdateRequest = bookingFormValuesToRequest(data)
    updateMutation.mutate(request)
  }

  const handleCancel = () => {
    router.navigate({ to: "/bookings/$id", params: { id } })
  }

  if (isLoading) {
    return <EditBookingPageSkeleton />
  }

  if (isError || !booking) {
    return (
      <div className="container max-w-5xl py-6">
        <div className="text-center">
          <p className="text-destructive">
            Failed to load booking. Please try again.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => router.navigate({ to: "/bookings" })}
          >
            Back to Bookings
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container max-w-5xl py-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) =>
            console.error("Form validation errors:", errors),
          )}
        >
          <div className="divide-y divide-border">
            {/* Guest Information Section */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0">
                <h2 className="text-base/7 font-semibold text-foreground">
                  Guest Information
                </h2>
                <p className="mt-1 text-sm/6 text-muted-foreground">
                  Enter the primary contact details and stay information for
                  this booking.
                </p>
              </div>

              <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
                <div className="px-4 py-6 sm:p-8">
                  <GuestInformationForm
                    allowPastDates
                    disabled={
                      form.formState.isSubmitting || updateMutation.isPending
                    }
                  />
                </div>
              </div>
            </div>

            {/* Water Taxi Section */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0">
                <h2 className="text-base/7 font-semibold text-foreground">
                  Water Taxi
                </h2>
                <p className="mt-1 text-sm/6 text-muted-foreground">
                  Schedule water taxi transportation for guest arrivals and
                  departures.
                </p>
              </div>

              <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
                <div className="px-4 py-6 sm:p-8">
                  <WaterTaxiForm
                    disabled={
                      form.formState.isSubmitting || updateMutation.isPending
                    }
                  />
                </div>
              </div>
            </div>

            {/* Kayak Rental Section */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0">
                <h2 className="text-base/7 font-semibold text-foreground">
                  Kayak Rentals
                </h2>
                <p className="mt-1 text-sm/6 text-muted-foreground">
                  Assign kayak equipment to individual guests with pickup and
                  return times.
                </p>
              </div>

              <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
                <div className="px-4 py-6 sm:p-8">
                  <KayakRentalForm
                    disabled={
                      form.formState.isSubmitting || updateMutation.isPending
                    }
                  />
                </div>
              </div>
            </div>

            {/* Kitchen Section */}
            <DietaryRequirementsSection
              form={form}
              disabled={form.formState.isSubmitting || updateMutation.isPending}
            />

            {/* Form Actions */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0" />
              <div className="md:col-span-2">
                <div className="flex items-center justify-end gap-x-6 px-4 sm:px-0">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={
                      form.formState.isSubmitting || updateMutation.isPending
                    }
                    className="text-sm/6 font-semibold text-foreground hover:text-foreground/80 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <LoadingButton
                    type="submit"
                    loading={
                      form.formState.isSubmitting || updateMutation.isPending
                    }
                  >
                    Save Changes
                  </LoadingButton>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
